import { t } from '@lingui/core/macro';
import { URLKeys, noddiAsync } from 'noddi-async';
import { NoddiButton, NoddiDialog, NoddiSkeleton } from 'noddi-ui-web';
import { formatCurrencyAmount, groupCars } from 'noddi-util';
import { tracking, useBookingAddress, useBookingSelectedSalesItemsCars, useBookingTimeWindow } from 'noddi-web';
import { useState } from 'react';
import { useMobileBookingCustomer } from '../../../hooks/useMobileBookingCustomer';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { ShoppingCartSummary } from '../../Summary/ShoppingCartSummary';

export const ShoppingCart = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { userGroupId } = useMobileBookingCustomer();
  const { currentBrand } = useCurrentBrand();
  const selectedAddress = useBookingAddress();
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const bookingTimeWindow = useBookingTimeWindow();

  const {
    data: shoppingCart,
    isLoading: isShoppingCartLoading,
    error: shoppingCartError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postGetShoppingCartForNewBooking,
    input: {
      addressId: selectedAddress?.id as number,
      cars: groupCars(selectedSalesItemsCars),
      deliveryWindow: bookingTimeWindow
        ? {
            id: bookingTimeWindow.deliveryWindowId,
            endsAt: bookingTimeWindow.endsAt,
            startsAt: bookingTimeWindow.startsAt
          }
        : undefined,
      userGroupId,
      brandDomain: currentBrand
    },
    queryConfig: {
      staleTime: Infinity,
      enabled: !!selectedAddress && !!selectedSalesItemsCars.length
    }
  });

  // The cart is a button in the fixed bottom bar, so a banner here deforms the bar on every step.
  // The same query backs the summary step, which surfaces the failure where it actually blocks.
  if (shoppingCartError) {
    return null;
  }

  if (isShoppingCartLoading) {
    return (
      // Sized and placed like the button it stands in for, so the bar does not jump sides when it loads.
      <div role='status' className='flex'>
        <span className='sr-only'>{t`Loading`}</span>
        <NoddiSkeleton variant='button' className='h-9 w-[123px]' />
      </div>
    );
  }

  if (!shoppingCart?.data) {
    return null;
  }

  const { totalPrice } = shoppingCart.data;
  const priceText = formatCurrencyAmount({ money: totalPrice, decimals: 0 });

  return (
    <>
      <NoddiDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        title={t`Your shopping cart`}
      >
        <ShoppingCartSummary shoppingCart={shoppingCart.data} />
      </NoddiDialog>

      <NoddiButton
        variant='outline'
        onClick={() => {
          tracking.track('shoppingCartOpened');
          setIsDialogOpen(true);
        }}
        startIcon='ShoppingCart'
        aria-label={t`Shopping cart, ${priceText}`}
      >
        {priceText}
      </NoddiButton>
    </>
  );
};
